import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Select,
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Form,
  InputNumber,
  Switch,
  Rate,
  Slider as AntSlider,
} from 'antd';
import {
  PlusOutlined,
  CloseCircleOutlined,
  HolderOutlined,
  DeleteOutlined,
  CopyOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import style from '../../Styles/AddUser.module.css';
import { FaArrowLeft } from 'react-icons/fa';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import SurveyCategoryApi from '../../Api/SurveyCategoryApi';
import { QuestionBank } from '../../Api/QuestionBankApi';
import { toastError, toastSuccess } from '../Toast/ToastComponent';
import { MdDeleteOutline } from 'react-icons/md';

const { Title, Text } = Typography;
const { Option } = Select;

const Q_TYPES = {
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  TEXT: 'text',
  MATRIX: 'matrix',
  SLIDER: 'slider',
  RATING: 'rating',
};

const DARK_BLUE = '#003a8c';
const HEADER_BLUE = '#f0f5f9';
const BORDER_COLOR = '#d9e2ec';
const TEXT_COLOR = '#334e68';
const DELETE_RED = '#ff4d4f';
const PRIMARY_COLOR = '#134780';

const styles = {
  addDarkBtn: {
    width: 28,
    height: 28,
    borderRadius: 4,
    background: '#134780',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  mainContentCard: {
    borderRadius: '8px',

    background: '#fff',
    padding: '20px',
  },
  questionCard: {
    marginBottom: 20,
    borderRadius: '8px',

    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    overflow: 'hidden',
    background: '#fff',
  },
  cardHeader: {
    background: '#f0f4f7',
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #dde3e8',
  },
  requiredToggleArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '13px',
    color: '#5c7080',
  },
  actionBtn: {
    width: 28,
    height: 28,
    background: '#fff',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  optionsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#DCE8F0',
    padding: '8px 16px',
    fontWeight: 600,
    color: '#334e68',
   
  },
  optionsWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4,1fr)',
    gap: '12px',
      marginTop: '20px'
  },
  optionInputBox: (hasError) => ({
    height: 34,
    width: '100%',
    background: '#fff',
    border: hasError ? '1px solid #ff4d4f' : '1px solid #d0dce3',
    borderRadius: 6,
    fontSize: 13,
  }),
  radioCircle: (type) => ({
    width: 14,
    height: 14,
    borderRadius: type === Q_TYPES.RADIO ? '50%' : '3px',
    border: `2px solid ${DARK_BLUE}`,
    marginRight: 6,
    flexShrink: 0,
  }),
  squarePlusBtn: {
    background: DARK_BLUE,
    borderColor: DARK_BLUE,
    borderRadius: '4px',
    width: '26px',
    height: '26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  addListBtn: {
    background: DARK_BLUE,
    borderColor: DARK_BLUE,
    color: '#fff',
    borderRadius: '4px',
    height: '34px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '500',
    width: 'fit-content',
  },
  nextActionBtn: {
    background: DARK_BLUE,
    borderColor: DARK_BLUE,
    color: '#fff',
    borderRadius: '4px',
    height: '34px',
    padding: '0 20px',
    fontSize: '13px',
    fontWeight: '500',
    width: 'fit-content',
    minWidth: 'auto',
  },
  copyBtn: (isHovered) => ({
    color: isHovered ? '#4096ff' : '#8c8c8c',
    borderColor: isHovered ? '#4096ff' : '#c8d3dc',
    backgroundColor: isHovered ? '#f5f5f5' : '#fff',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  }),
  deleteBtn: (isHovered) => ({
    color: isHovered ? '#ff4d4f' : '#ff4d4f',
    borderColor: isHovered ? '#ff4d4f' : '#ff7875',
    backgroundColor: isHovered ? '#fff1f0' : '#fff',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  }),
  closeIconSmall: {
    color: '#8c8c8c',
    cursor: 'pointer',
    fontSize: 16,
    marginLeft: '4px',
  },
  sectionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    background: '#DCE8F0',
    padding: '8px 14px',
    borderRadius: 4,
    marginBottom: 10,
    fontWeight: 600,
    fontSize: 14,
    color: '#2b4953',
  },
};

const createBaseQuestion = () => ({
  id: `new-${Date.now()}`,
  questionText: '',
  type: Q_TYPES.RADIO,
  required: true,
  activeStatus: true,
  options: [{ title: '' }, { title: '' }, { title: '' }, { title: '' }],
  ratingScale: 5,
  sliderMin: 0,
  sliderMax: 100,
  matrixRows: [{ title: '' }, { title: '' }, { title: '' }, { title: '' }],
  matrixCols: [{ title: '' }, { title: '' }, { title: '' }, { title: '' }],
});

const AddQuestionBank = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([createBaseQuestion()]);
  const [errors, setErrors] = useState({});
  const questionRefs = useRef({});

  const isEditMode = !!id && id !== '0';

  useEffect(() => {
    fetchCategories();
    const dataToLoad = location.state?.oldData || location.state?.question;
    if (dataToLoad) loadExistingQuestion(dataToLoad);
  }, [location.state, id]);

  // Auto-scroll to first error field when errors change
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) return;

    const firstErrorKey = Object.keys(errors)[0];

    // Find the element with error
    let elementId = null;
    let questionId = null;

    if (firstErrorKey.includes('_opt_')) {
      // Option error: format is "questionId_opt_index"
      const parts = firstErrorKey.split('_opt_');
      questionId = parts[0];
      elementId = `option-${firstErrorKey}`;
    } else if (firstErrorKey.includes('_row_')) {
      // Matrix row error
      const parts = firstErrorKey.split('_row_');
      questionId = parts[0];
      elementId = `matrix-row-${firstErrorKey}`;
    } else if (firstErrorKey.includes('_col_')) {
      // Matrix column error
      const parts = firstErrorKey.split('_col_');
      questionId = parts[0];
      elementId = `matrix-col-${firstErrorKey}`;
    } else {
      // Question text error
      questionId = firstErrorKey;
      elementId = `question-${firstErrorKey}`;
    }

    // Scroll to the element
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.transition = 'box-shadow 0.3s';
        element.style.boxShadow = '0 0 0 2px rgba(255,77,79,0.4)';
        setTimeout(() => {
          element.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  }, [errors]);

  const fetchCategories = async () => {
    try {
      const res = await SurveyCategoryApi.getDropdown();
      if (res?.data?.success) setCategories(res.data.result);
    } catch (err) {
      toastError('Failed to load categories');
    }
  };

  const loadExistingQuestion = (q) => {
    const qType = (q.question_type || q.type || Q_TYPES.RADIO).toLowerCase();
    let parsedOptions = [];
    let parsedRows = [{ title: '' }];
    let parsedCols = [{ title: '' }];
    let ratingVal = 5;
    let sliderMinVal = 0;
    let sliderMaxVal = 100;

    try {
      let rawOptions = q.option || q.options;
      if (typeof rawOptions === 'string') {
        try {
          rawOptions = JSON.parse(rawOptions);
        } catch (e) {}
      }

      if (qType === Q_TYPES.MATRIX) {
        if (rawOptions?.rows)
          parsedRows = rawOptions.rows.map((r) => ({
            title: typeof r === 'string' ? r : r.title || '',
          }));
        if (rawOptions?.columns)
          parsedCols = rawOptions.columns.map((c) => ({
            title: typeof c === 'string' ? c : c.title || '',
          }));
      } else if (qType === Q_TYPES.RATING) {
        if (Array.isArray(rawOptions) && rawOptions[0]?.title) {
          ratingVal = Number(rawOptions[0].title);
        } else if (rawOptions?.title) {
          ratingVal = Number(rawOptions.title);
        }
      } else if (qType === Q_TYPES.SLIDER) {
        const sliderSettings = Array.isArray(rawOptions)
          ? rawOptions[0]
          : rawOptions;
        sliderMinVal = sliderSettings?.min ?? 0;
        sliderMaxVal = sliderSettings?.max ?? 100;
      } else if (qType === Q_TYPES.RADIO || qType === Q_TYPES.CHECKBOX) {
        const optionsArray = Array.isArray(rawOptions) ? rawOptions : [];
        parsedOptions = optionsArray.map((o) =>
          typeof o === 'string' ? { title: o } : o,
        );
        while (parsedOptions.length < 4) {
          parsedOptions.push({ title: '' });
        }
      }
    } catch (e) {
      console.error('Parse error', e);
    }

    setQuestions([
      {
        id: q.id || Date.now(),
        questionText: q.question || q.questionText || '',
        type: qType,
        required:
          q.is_required === 1 || q.is_required === true || q.required === true,
        activeStatus:
          q.activeStatus === false ||
          q.activeStatus === 'false' ||
          q.activeStatus === 0
            ? false
            : true,
        options:
          parsedOptions.length > 0
            ? parsedOptions
            : [{ title: '' }, { title: '' }, { title: '' }, { title: '' }],
        ratingScale: ratingVal,
        sliderMin: sliderMinVal,
        sliderMax: sliderMaxVal,
        matrixRows: parsedRows,
        matrixCols: parsedCols,
      },
    ]);
    form.setFieldsValue({ category_id: q.category_id });
  };

  const onFinish = async (values) => {
    try {
      await form.validateFields();
    } catch (err) {
      form.scrollToField('category_id', {
        behavior: 'smooth',
        block: 'center',
      });

      setTimeout(() => {
        const el = document.querySelector('[id="category_id"]');
        if (el) el.focus();
      }, 100);

      toastError('Please select a category');
      return;
    }
    let newErrs = {};
    let hasError = false;
    let errorMsg = 'Please complete all fields before save.';

    questions.forEach((q) => {
      if (!q.questionText.trim()) {
        newErrs[q.id] = true;
        hasError = true;
        errorMsg = 'Please complete all fields before save.';
      }

      if (q.type === 'radio' || q.type === 'checkbox') {
        const filledOptions = q.options.filter((o) => o.title.trim());
        if (filledOptions.length < 2) {
          errorMsg = 'Please complete all fields before save.';
          hasError = true;
        }

        // Duplicate check for options
        const optionTitles = q.options.map(o => o.title.trim().toLowerCase()).filter(Boolean);
        const uniqueOptions = new Set(optionTitles);
        if (uniqueOptions.size !== optionTitles.length) {
          hasError = true;
          errorMsg = 'Duplicate options are not allowed.';
          // Highlight duplicate fields
          const seen = new Set();
          q.options.forEach((o, i) => {
            const title = o.title.trim().toLowerCase();
            if (title && seen.has(title)) {
              newErrs[`${q.id}_opt_${i}`] = true;
            }
            seen.add(title);
          });
        }

        q.options.forEach((o, i) => {
          if (!o.title.trim()) {
            newErrs[`${q.id}_opt_${i}`] = true;
            hasError = true;
          }
        });
      }

      if (q.type === 'matrix') {
        const filledRows = q.matrixRows.filter((r) => r.title.trim());
        if (filledRows.length < 1) {
          errorMsg = 'At least one matrix row is required';
          hasError = true;
        }

        // Duplicate check for matrix rows
        const rowTitles = q.matrixRows.map(r => r.title.trim().toLowerCase()).filter(Boolean);
        const uniqueRows = new Set(rowTitles);
        if (uniqueRows.size !== rowTitles.length) {
          hasError = true;
          errorMsg = 'Duplicate matrix rows are not allowed.';
          const seen = new Set();
          q.matrixRows.forEach((r, i) => {
            const title = r.title.trim().toLowerCase();
            if (title && seen.has(title)) {
              newErrs[`${q.id}_row_${i}`] = true;
            }
            seen.add(title);
          });
        }

        q.matrixRows.forEach((r, i) => {
          if (!r.title.trim()) {
            newErrs[`${q.id}_row_${i}`] = true;
            hasError = true;
          }
        });

        const filledCols = q.matrixCols.filter((c) => c.title.trim());
        if (filledCols.length < 1) {
          errorMsg = 'At least one matrix column is required';
          hasError = true;
        }

        // Duplicate check for matrix columns
        const colTitles = q.matrixCols.map(c => c.title.trim().toLowerCase()).filter(Boolean);
        const uniqueCols = new Set(colTitles);
        if (uniqueCols.size !== colTitles.length) {
          hasError = true;
          errorMsg = 'Duplicate matrix columns are not allowed.';
          const seen = new Set();
          q.matrixCols.forEach((c, i) => {
            const title = c.title.trim().toLowerCase();
            if (title && seen.has(title)) {
              newErrs[`${q.id}_col_${i}`] = true;
            }
            seen.add(title);
          });
        }

        q.matrixCols.forEach((c, i) => {
          if (!c.title.trim()) {
            newErrs[`${q.id}_col_${i}`] = true;
            hasError = true;
          }
        });
      }
    });

    if (hasError) {
      setErrors(newErrs);
      toastError(errorMsg);
      return;
    }
    setErrors({});
    try {
      setLoading(true);
      const formattedQuestions = questions.map((q) => {
        let finalOptions;
        if (q.type === Q_TYPES.MATRIX) {
          finalOptions = {
            rows: q.matrixRows
              .filter((r) => r.title?.trim())
              .map((r) => ({ title: r.title })),
            columns: q.matrixCols
              .filter((c) => c.title?.trim())
              .map((c) => ({ title: c.title })),
          };
        } else if (q.type === Q_TYPES.SLIDER) {
          finalOptions = [{ min: q.sliderMin, max: q.sliderMax }];
        } else if (q.type === Q_TYPES.RATING) {
          finalOptions = [{ title: q.ratingScale }];
        } else if (q.type === Q_TYPES.TEXT) {
          finalOptions = [];
        } else {
          finalOptions = q.options
            .filter((o) => o.title?.trim())
            .map((o) => ({ title: o.title }));
        }
        return {
          title: q.questionText,
          is_required: q.required,
          likert_scale: { type: q.type, options: finalOptions },
          id: q.id,
          activeStatus: String(q.activeStatus),
        };
      });

      let res;
      if (isEditMode) {
        const q = formattedQuestions[0];
        res = await QuestionBank.updateQuestion({
          id: id,
          category_id: values.category_id,
          question: q.title,
          question_type: q.likert_scale.type,
          option: JSON.stringify(q.likert_scale.options),
          is_required: q.is_required ? 1 : 0,
          activeStatus: q.activeStatus,
        });
      } else {
        res = await QuestionBank.createQuestion({
          title: 'Survey',
          category_id: values.category_id,
          list: formattedQuestions,
        });
      }
      if (res?.data?.success) {
        toastSuccess(
          isEditMode
            ? 'Question Updated Success!'
            : 'QuestionBank Created Success!',
        );
        navigate('/questionbank');
      }
    } catch (error) {
      toastError('API Error');
    } finally {
      setLoading(false);
    }
  };

  const updateQuestionData = (qId, field, value) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === qId ? { ...q, [field]: value } : q)),
    );
    if (field === 'questionText' && errors[qId]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[qId];
        return copy;
      });
    }
  };

  const handleNestedUpdate = (qId, field, idx, value) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === qId) {
          const newList = [...q[field]];
          newList[idx] = { ...newList[idx], title: value };
          return { ...q, [field]: newList };
        }
        return q;
      }),
    );

    let errKey = '';
    if (field === 'options') errKey = `${qId}_opt_${idx}`;
    if (field === 'matrixRows') errKey = `${qId}_row_${idx}`;
    if (field === 'matrixCols') errKey = `${qId}_col_${idx}`;

    if (errKey && errors[errKey]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[errKey];
        return copy;
      });
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(questions);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);
    setQuestions(items);
  };

  const renderMatrixEditor = (q) => (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
          marginBottom: 12,
 
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <div style={styles.optionsHeader}>
          <span style={{ fontSize: '13px' }}>Questions</span>
          <Button
            type="primary"
            size="small"
            style={styles.squarePlusBtn}
            icon={<PlusOutlined />}
            className="hover-add-btn"
            onClick={() =>
              updateQuestionData(q.id, 'matrixRows', [
                ...q.matrixRows,
                { title: '' },
              ])
            }
          />
        </div>
        <div style={{ marginTop: "12px" }}>
          {q.matrixRows.map((row, i) => (
            <Input
              key={i}
              id={`matrix-row-${q.id}_row_${i}`}
              placeholder="Enter Your Question"
              status={errors[`${q.id}_row_${i}`] ? 'error' : ''}
              value={row.title}
              style={{ marginBottom: 6, height: 32, fontSize: '13px' }}
              onChange={(e) =>
                handleNestedUpdate(q.id, 'matrixRows', i, e.target.value)
              }
              suffix={
                q.matrixRows.length > 1 && (
                  <div
                    className="hover-remove-icon"
                    onClick={() =>
                      updateQuestionData(
                        q.id,
                        'matrixRows',
                        q.matrixRows.filter((_, idx) => idx !== i),
                      )
                    }
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      border: '1px solid #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      background: '#f5f5f5',
                      transition: 'all 0.3s',
                      flexShrink: 0,
                    }}
                  >
                    <CloseOutlined style={{ fontSize: 9, color: '#888' }} />
                  </div>
                )
              }
            />
          ))}
        </div>
      </div>

      <div
        style={{
      
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <div style={styles.optionsHeader}>
          <span style={{ fontSize: '13px' }}>Options</span>
          <Button
            type="primary"
            size="small"
            style={styles.squarePlusBtn}
            icon={<PlusOutlined />}
            className="hover-add-btn"
            onClick={() =>
              updateQuestionData(q.id, 'matrixCols', [
                ...q.matrixCols,
                { title: '' },
              ])
            }
          />
        </div>
        <div style={{ marginTop: "13px" }}>
          {q.matrixCols.map((col, i) => (
            <Input
              key={i}
              id={`matrix-col-${q.id}_col_${i}`}
              placeholder="Enter Your Option"
              status={errors[`${q.id}_col_${i}`] ? 'error' : ''}
              value={col.title}
              style={{ marginBottom: 6, height: 32, fontSize: '13px' }}
              onChange={(e) =>
                handleNestedUpdate(q.id, 'matrixCols', i, e.target.value)
              }
              suffix={
                q.matrixCols.length > 1 && (
                  <div
                    className="hover-remove-icon"
                    onClick={() =>
                      updateQuestionData(
                        q.id,
                        'matrixCols',
                        q.matrixCols.filter((_, idx) => idx !== i),
                      )
                    }
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      border: '1px solid #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      background: '#f5f5f5',
                      transition: 'all 0.3s',
                      flexShrink: 0,
                    }}
                  >
                    <CloseOutlined style={{ fontSize: 9, color: '#888' }} />
                  </div>
                )
              }
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderSliderEditor = (q) => (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
    
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <div style={styles.optionsHeader}>
          <span style={{ fontSize: '13px' }}>Slider Range</span>
        </div>
        <div style={{ padding: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Text style={{ fontSize: '12px', color: '#666' }}>Min Value</Text>
              <InputNumber
                min={0}
                max={q.sliderMax - 1}
                value={q.sliderMin}
                onChange={(val) => updateQuestionData(q.id, 'sliderMin', val)}
                style={{ width: '100%', marginTop: 4 }}
              />
            </Col>
            <Col span={12}>
              <Text style={{ fontSize: '12px', color: '#666' }}>Max Value</Text>
              <InputNumber
                min={q.sliderMin + 1}
                max={1000}
                value={q.sliderMax}
                onChange={(val) => updateQuestionData(q.id, 'sliderMax', val)}
                style={{ width: '100%', marginTop: 4 }}
              />
            </Col>
          </Row>
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                flex: 1,
                position: 'relative',
                height: 6,
                background: '#e0e6ea',
                borderRadius: 3,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '50%',
                  height: '100%',
                  background: DARK_BLUE,
                  borderRadius: 3,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: 'calc(50% - 10px)',
                  top: -7,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: DARK_BLUE,
                  boxShadow: '0 2px 6px rgba(19,71,128,0.35)',
                  border: '2px solid #fff',
                  cursor: 'default',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 4,
              }}
            >
              <Text style={{ fontSize: '11px', color: '#999' }}>
                {q.sliderMin}
              </Text>
              <Text style={{ fontSize: '11px', color: '#999' }}>
                {q.sliderMax}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRatingEditor = (q) => (
    <div style={{ marginTop: 12 }}>
      <div
        style={{
   
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        <div style={styles.optionsHeader}>
          <span style={{ fontSize: '13px' }}>Rating Scale</span>
        </div>
        <div style={{ padding: '16px' }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Text style={{ fontSize: '12px', color: '#666' }}>
                Number of Stars
              </Text>
              <InputNumber
                min={1}
                max={10}
                value={q.ratingScale}
                onChange={(val) => updateQuestionData(q.id, 'ratingScale', val)}
                style={{ width: '100%', marginTop: 4 }}
              />
            </Col>
            <Col span={16}>
              <Text style={{ fontSize: '12px', color: '#666' }}>Preview</Text>
              <div style={{ marginTop: 8 }}>
                <Rate
                  disabled
                  value={q.ratingScale}
                  count={q.ratingScale}
                  style={{ fontSize: 18, color: '#fadb14' }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <style>{`
        /* Internal CSS - Copy Button Hover Effect */
        .hover-copy-icon {
          transition: all 0.2s ease !important;
        }
        
        .hover-copy-icon:hover {
          color: #2f80ed !important;
          transform: scale(1.2) !important;
        }
        
        /* Other existing styles */
        .hover-action-btn, .hover-delete-btn, .hover-remove-icon, .hover-add-btn { 
          transition: all 0.3s ease; 
        }
        .hover-action-btn:hover { 
          background-color: #e6f0fb !important; 
        }
        .hover-delete-btn:hover { 
          background-color: #ffe6e6 !important; 
          border-color: #ff4d4f !important; 
        }
        .hover-remove-icon:hover { 
          background-color: #d9d9d9 !important; 
        }
        .hover-add-btn:hover { 
          background-color: #2f80ed !important; 
          box-shadow: 0 4px 12px rgba(47, 128, 237, 0.35);
        }
        .error-bg-hover:hover { 
          background-color: #ffe0e0 !important; 
        }
        
        /* Error highlight animation */
        @keyframes errorPulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4); }
          70% { box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.4); }
          100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
        }
        
        .error-highlight {
          animation: errorPulse 0.5s ease-in-out;
        }
      `}</style>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      >
        <div style={{ width: '100%' }}>
          <div
            className="d-flex px-0 pb-1 align-items-center"
            style={{ marginBottom: '20px' }}
          >
            <Space size="middle">
              <div
                className={`${style['arrow-icon']} border px-2 pb-1`}
                onClick={() => navigate(-1)}
                style={{ cursor: 'pointer' }}
              >
                <FaArrowLeft />
              </div>
              <div className={`mx-3 ${style['add-user']} mb-0`}>
                {isEditMode ? 'Edit Question' : 'Create Question'}
              </div>
            </Space>
          </div>

          <Row
            gutter={16}
            align="middle"
            style={{ marginBottom: 20, marginLeft: 20 }}
          >
            <Col>
              <Text style={{ color: '#6c757d', fontWeight: '500' }} strong>
                Category:
              </Text>
            </Col>
            <Col span={6}>
              <Form.Item
                name="category_id"
                rules={[
                  { required: true, message: 'Please select a category' },
                ]}
                style={{ marginBottom: 0 }}
              >
                <Select placeholder="Select Category">
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.categoryName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              background: '#fff',
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: '8px',
              padding: '20px',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions-list">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {questions.map((q, idx) => (
                      <Draggable
                        key={q.id}
                        draggableId={String(q.id)}
                        index={idx}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            id={`question-${q.id}`}
                          >
                            <div
                              style={{
                            border: `1px solid ${BORDER_COLOR}`,
                                borderRadius: '8px',
                                marginBottom: 20,
                                background: '#fff',
                                overflow: 'hidden',
                              }}
                            >
                              <div style={styles.cardHeader}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                  }}
                                >
                                  <span {...dragProvided.dragHandleProps}>
                                    <HolderOutlined
                                      style={{
                                        cursor: 'grab',
                                        color: '#9ba8b5',
                                        fontSize: 12,
                                      }}
                                    />
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: 13,
                                      color: '#2b4953',
                                    }}
                                  >
                                    Q{idx + 1}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                  }}
                                >
                                  <div style={styles.requiredToggleArea}>
                                    <Switch
                                      checked={q.required}
                                      onChange={(val) =>
                                        updateQuestionData(
                                          q.id,
                                          'required',
                                          val,
                                        )
                                      }
                                    />
                                    <span>Required</span>
                                  </div>
                                  {!isEditMode && (
                                    <button
                                      type="button"
                                      title="Duplicate Question"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        const index = questions.findIndex(
                                          (item) => item.id === q.id,
                                        );
                                        const newQuestion = {
                                          ...q,
                                          id: `new-${Date.now()}`,
                                        };
                                        const updated = [...questions];
                                        updated.splice(
                                          index + 1,
                                          0,
                                          newQuestion,
                                        );
                                        setQuestions(updated);
                                      }}
                                      style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <CopyOutlined
                                        className="hover-copy-icon"
                                        style={{
                                          color: '#134780',
                                          fontSize: 14,
                                        }}
                                      />
                                    </button>
                                  )}
                                  {!isEditMode && (
                                    <button
                                      title="Delete Question"
                                      onClick={() => {
                                        if (questions.length === 1) {
                                          toastError(
                                            'Cannot delete the only question. At least one question is required.',
                                          );
                                          return;
                                        }
                                        setQuestions(
                                          questions.filter(
                                            (item) => item.id !== q.id,
                                          ),
                                        );
                                      }}
                                      style={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        padding: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      className="hover-delete-btn"
                                    >
                                      <MdDeleteOutline
                                        size={18}
                                        style={{ color: '#ff4d4f' }}
                                      />
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div
                                style={{
                                  padding: '12px 14px',
                                  display: 'flex',
                                  gap: 8,
                                }}
                              >
                                <Input
                                  placeholder="Enter your Question"
                                  status={errors[q.id] ? 'error' : ''}
                                  value={q.questionText}
                                  onChange={(e) =>
                                    updateQuestionData(
                                      q.id,
                                      'questionText',
                                      e.target.value,
                                    )
                                  }
                                  style={{ flex: 1 }}
                                />
                                <Select
                                  value={q.type}
                                  onChange={(v) =>
                                    updateQuestionData(q.id, 'type', v)
                                  }
                                  style={{ width: 140 }}
                                >
                                  <Option value="radio">Radio</Option>
                                  <Option value="checkbox">Check Box</Option>
                                  <Option value="text">Free Text</Option>
                                  <Option value="matrix">Matrix</Option>
                                  <Option value="slider">Slider</Option>
                                  <Option value="rating">Rating</Option>
                                </Select>
                              </div>

                              <div style={{ padding: '0 14px 14px 14px' }}>
                                {(q.type === 'radio' ||
                                  q.type === 'checkbox') && (
                                  <div
                                    style={{
                                  
                                      borderRadius: '6px',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <div style={styles.optionsHeader}>
                                      <span style={{ fontSize: '13px' }}>
                                        Options
                                      </span>
                                      <Button
                                        type="primary"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        style={styles.addDarkBtn}
                                        className="hover-add-btn"
                                        onClick={() =>
                                          updateQuestionData(q.id, 'options', [
                                            ...q.options,
                                            { title: '' },
                                          ])
                                        }
                                      />
                                    </div>
                                    <div style={styles.optionsWrapper}>
                                      {q.options.map((o, i) => {
                                        const hasError =
                                          !!errors[`${q.id}_opt_${i}`];
                                        return (
                                          <div
                                            key={i}
                                            id={`option-${q.id}_opt_${i}`}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 6,
                                              background: '#fff',
                                              border: hasError
                                                ? '1px solid #ff4d4f'
                                                : '1px solid #d0dce3',
                                              borderRadius: 6,
                                              padding: '5px 10px',
                                              flex: 1,
                                            }}
                                          >
                                            <div
                                              style={styles.radioCircle(q.type)}
                                            />
                                            <input
                                              placeholder="Enter your Choice"
                                              value={o.title}
                                              onChange={(e) =>
                                                handleNestedUpdate(
                                                  q.id,
                                                  'options',
                                                  i,
                                                  e.target.value,
                                                )
                                              }
                                              style={{
                                                border: 'none',
                                                outline: 'none',
                                                background: 'transparent',
                                                fontSize: 13,
                                                color: '#2b4953',
                                                flex: 1,
                                                minWidth: 0,
                                                width: '100%',
                                              }}
                                            />
                                            {q.options.length > 1 && (
                                              <div
                                                onClick={() =>
                                                  updateQuestionData(
                                                    q.id,
                                                    'options',
                                                    q.options.filter(
                                                      (_, idx) => idx !== i,
                                                    ),
                                                  )
                                                }
                                                className="hover-remove-icon"
                                                style={{
                                                  width: 18,
                                                  height: 18,
                                                  borderRadius: 9,
                                                  border: '1px solid #ccc',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  cursor: 'pointer',
                                                  flexShrink: 0,
                                                  background: '#f5f5f5',
                                                }}
                                              >
                                                <CloseOutlined
                                                  style={{
                                                    fontSize: 9,
                                                    color: '#888',
                                                  }}
                                                />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {q.type === Q_TYPES.MATRIX &&
                                  renderMatrixEditor(q)}
                                {q.type === Q_TYPES.SLIDER &&
                                  renderSliderEditor(q)}
                                {q.type === Q_TYPES.RATING &&
                                  renderRatingEditor(q)}
                                {q.type === Q_TYPES.TEXT && (
                                  <div
                                    style={{
                                      marginTop: 12,
                                      background: '#f5f5f5',
                                      padding: 12,
                                      borderRadius: 6,
                                    }}
                                  >
                                    <Input.TextArea
                                      placeholder="User response text area..."
                                      rows={3}
                                      disabled
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <div
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: isEditMode ? 'flex-end' : 'space-between',
              }}
            >
              {/* <Button
                //  type="primary"
                icon={<PlusOutlined />}
                onClick={() =>
                  setQuestions([...questions, createBaseQuestion()])
                }
              >
                Add Question
              </Button> */}
              {!isEditMode && (
                <Button
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setQuestions([...questions, createBaseQuestion()])
                  }
                >
                  Add Question
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ width: '100px' }}
              >
                {isEditMode ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default AddQuestionBank;
